// AlertScreeningPanel.tsx — AF-SS-072.
// Inline explainability panel rendered on AlertDetail for alerts
// generated by the screening pipeline. Shows the matched entity,
// score breakdown, alias indicators, dataset provenance, and the
// current disposition state. Empty state renders nothing — most
// alerts aren't screening-driven, the panel just doesn't surface.

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, ChevronRight, ShieldAlert, ShieldCheck } from 'lucide-react'
import { Link } from 'react-router-dom'
import { apiJson } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/cn'

interface AlertMatchView {
  match_public_id: string
  screening_public_id: string
  list_code: string
  list_type?: string
  dataset_version: string
  dataset_age_seconds: number
  entity_canonical_name: string
  entity_kind?: string
  composite_score: number
  score_band: 'weak' | 'review' | 'block'
  components?: Record<string, number>
  alias_used: boolean
  alias_matched?: string
  disposition?: string
  dispositioned_at?: string
  dispositioned_by_id?: number
  disposition_approved_at?: string
  disposition_approved_by_id?: number
  screening_requested_at: string
}

interface AlertMatchesResponse {
  alert_id: number
  items: AlertMatchView[]
  count: number
}

async function fetchAlertMatches(alertID: string | number): Promise<AlertMatchesResponse> {
  return apiJson(`/screening/alerts/${alertID}/matches`)
}

export function AlertScreeningPanel({ alertID }: { alertID: string | number }) {
  const q = useQuery({
    queryKey: ['screening', 'alert-matches', alertID],
    queryFn: () => fetchAlertMatches(alertID),
    refetchInterval: 30_000,
    enabled: !!alertID,
  })

  // Don't even render the card when there's nothing to show — most
  // alerts aren't screening-driven; rendering an empty "no matches"
  // card every time would clutter the detail page.
  if (q.isLoading) return null
  if (q.error) return null
  if (!q.data || q.data.items.length === 0) return null

  return (
    <Card className="border-amber-200 bg-amber-50/30">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-600" />
            <CardTitle>Sanctions / PEP screening</CardTitle>
            <Badge variant="secondary" className="font-mono text-xs">
              {q.data.count} {q.data.count === 1 ? 'match' : 'matches'}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-slate-600">
          This alert was generated by the screening engine. Each row below is one provider hit;
          the maker-checker disposition flow lives at the bottom.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {q.data.items.map((m) => (
          <MatchRow key={m.match_public_id} m={m} />
        ))}
      </CardContent>
    </Card>
  )
}

function MatchRow({ m }: { m: AlertMatchView }) {
  const components = useMemo(() => Object.entries(m.components ?? {}), [m.components])
  const dispoLabel = formatDisposition(m)
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="text-base font-semibold text-slate-900">{m.entity_canonical_name}</span>
          <span className="font-mono text-xs text-slate-500">{m.list_code}</span>
          {m.alias_used && (
            <Badge variant="outline" className="text-xs">
              alias: {m.alias_matched || '—'}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ScoreBandBadge band={m.score_band} score={m.composite_score} />
          {dispoLabel && (
            <Badge variant={dispoVariant(m.disposition)} className="text-xs">
              {dispoLabel}
            </Badge>
          )}
        </div>
      </div>

      {components.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-5">
          {components.map(([key, value]) => (
            <ComponentBar key={key} label={key} value={value} />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2 text-xs text-slate-500">
        <div className="flex items-center gap-3">
          <span>
            dataset <span className="font-mono text-slate-700">{m.dataset_version}</span>
          </span>
          <span>· age {fmtAge(m.dataset_age_seconds)}</span>
          <span>· screened {timeAgo(m.screening_requested_at)}</span>
        </div>
        <Link
          to={`/screening/screenings/${m.screening_public_id}`}
          className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        >
          Full explainability <ChevronRight size={12} />
        </Link>
      </div>
    </div>
  )
}

function ScoreBandBadge({ band, score }: { band: 'weak' | 'review' | 'block'; score: number }) {
  const map = {
    weak:   { cls: 'bg-slate-100 text-slate-700 border-slate-200',   Icon: ShieldCheck },
    review: { cls: 'bg-amber-50 text-amber-800 border-amber-200',     Icon: AlertCircle },
    block:  { cls: 'bg-red-50 text-red-800 border-red-200',           Icon: ShieldAlert },
  }
  const m = map[band] ?? map.weak
  const Icon = m.Icon
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium tabular-nums', m.cls)}>
      <Icon size={12} />
      {band} · {(score * 100).toFixed(1)}%
    </span>
  )
}

function ComponentBar({ label, value }: { label: string; value: number }) {
  const pct = Math.max(0, Math.min(100, value * 100))
  return (
    <div>
      <div className="flex justify-between">
        <span className="font-mono text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-700">{pct.toFixed(0)}%</span>
      </div>
      <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function formatDisposition(m: AlertMatchView): string | null {
  if (!m.disposition) return null
  const base = m.disposition.replace(/_/g, ' ')
  if (m.disposition_approved_at) return `${base} (approved)`
  return `${base} (pending checker)`
}

function dispoVariant(d?: string): 'default' | 'secondary' | 'destructive' {
  switch (d) {
    case 'true_match':
    case 'pep_confirmed':
      return 'destructive'
    case 'false_positive':
    case 'pep_false_positive':
      return 'secondary'
    default:
      return 'default'
  }
}

function fmtAge(seconds: number): string {
  if (!seconds || seconds <= 0) return '—'
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86_400)}d`
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`
  return new Date(iso).toLocaleDateString()
}
